<template>
  <div :class="['auto-email-gss-dataset-page', { 'rmr-page-shell pa-6': !embedded, 'pa-0': embedded }]">
    <!-- Header Hero Banner (RMR Design System Style - exact match with mother page) -->
    <rmr-page-hero
      v-if="!embedded"
      title="Données"
      :title-accent="pageTitle"
      :subtitle="`Consultation et surcharge manuelle des métriques de la table ${datasetName.toUpperCase()} issues de GSS.`"
      :icon="icons.mdiDatabaseSync"
      class="mb-6"
    >
      <template #actions>
        <v-btn
          color="white"
          class="primary--text font-weight-bold rounded-pill shadow-btn"
          :loading="syncing"
          @click="openConfirmSyncModal"
        >
          <v-icon left color="primary">{{ icons.mdiRefresh }}</v-icon>
          Synchroniser les données
        </v-btn>
      </template>
    </rmr-page-hero>

    <!-- Historique des Synchronisations GSS Card pour cet onglet (Affiché en premier) -->
    <v-card :class="['mb-6 rounded-lg', embedded ? 'elevation-0 embedded-card-border' : 'elevation-2']">
      <v-card-title class="d-flex justify-space-between align-center py-3 px-4 border-bottom">
        <div class="d-flex align-center">
          <v-icon class="mr-2" color="primary">{{ icons.mdiDatabaseSync }}</v-icon>
          <span class="text-subtitle-1 font-weight-bold primary--text">
            Historique des synchronisations GSS ({{ pageTitle }})
          </span>
        </div>
        <div class="d-flex align-center">
          <v-btn icon small color="primary" class="mr-2" :loading="loadingGssSyncHistory" @click="fetchGssSyncHistory">
            <v-icon small>{{ icons.mdiRefresh }}</v-icon>
          </v-btn>
          <v-btn icon small @click="showGssSyncHistory = !showGssSyncHistory">
            <v-icon small>{{ showGssSyncHistory ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-expand-transition>
        <div v-show="showGssSyncHistory">
          <v-card-text class="pa-4">
            <v-data-table
              :headers="gssSyncHistoryHeaders"
              :items="gssSyncHistoryList"
              :loading="loadingGssSyncHistory"
              dense
              class="elevation-0"
              :items-per-page="5"
              :footer-props="{ 'items-per-page-options': [5, 10, 25] }"
              no-data-text="Aucune synchronisation GSS enregistrée pour cet onglet"
            >
              <template v-slot:item.created_at="{ item }">
                <span class="font-weight-medium">{{ formatDateTime(item.created_at) }}</span>
              </template>

              <template v-slot:item.triggered_by="{ item }">
                <v-chip
                  small
                  class="font-weight-bold"
                  :style="isSystemTriggered(item) ? 'background-color: #f1f5f9 !important; border: 1px solid #cbd5e1;' : 'background-color: #e0e7ff !important; border: 1px solid #c7d2fe;'"
                >
                  <v-icon left x-small :style="isSystemTriggered(item) ? 'color: #475569 !important;' : 'color: #4338ca !important;'">
                    {{ isSystemTriggered(item) ? icons.mdiRobot : icons.mdiAccount }}
                  </v-icon>
                  <span :style="isSystemTriggered(item) ? 'color: #1e293b !important; font-weight: 600;' : 'color: #1e1b4b !important; font-weight: 600;'">
                    {{ item.user_name || item.user_email || item.triggered_by || 'Manuel' }}
                  </span>
                </v-chip>
              </template>

              <template v-slot:item.target_tab="{ item }">
                <v-chip small color="#2c3e50" dark class="font-weight-bold px-3">
                  {{ formatTabName(item.target_tab) }}
                </v-chip>
              </template>

              <template v-slot:item.status="{ item }">
                <div class="d-flex align-center">
                  <div
                    v-if="item.status === 'in_progress'"
                    class="d-flex align-center px-3 py-1 rounded-pill status-in-progress-pill"
                  >
                    <v-progress-circular
                      indeterminate
                      size="14"
                      width="2"
                      color="warning darken-3"
                      class="mr-2"
                    ></v-progress-circular>
                    <span class="text-caption font-weight-bold warning--text text--darken-4">En cours...</span>
                  </div>
                  <v-chip
                    v-else-if="item.status === 'success'"
                    small
                    color="success"
                    outlined
                    class="font-weight-bold"
                  >
                    Succès
                  </v-chip>
                  <v-chip
                    v-else
                    small
                    color="error"
                    outlined
                    class="font-weight-bold"
                  >
                    Échec
                  </v-chip>
                  <v-icon
                    v-if="item.error_message && item.status !== 'in_progress'"
                    small
                    color="error"
                    class="ml-2"
                    :title="item.error_message"
                  >
                    {{ icons.mdiAlertCircle }}
                  </v-icon>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- Main Card Content (PJ 4 style - Vue d'ensemble des données) -->
    <v-card :class="['rounded-lg', embedded ? 'pa-4 pa-md-6 elevation-0 embedded-card-border' : 'pa-6 elevation-2']">
      <div class="d-flex align-center justify-space-between mb-4 flex-wrap">
        <h2 class="text-h6 font-weight-bold primary--text mb-2 mb-sm-0">
          Vue d'ensemble des données
          <v-chip color="primary" class="ml-2 font-weight-bold" small label>{{ totalItems }}</v-chip>
        </h2>

        <!-- Filters Row -->
        <div class="d-flex align-center flex-wrap" style="gap: 12px;">
          <v-text-field
            v-model="searchSite"
            label="Recherche"
            placeholder="Rechercher un site"
            dense
            outlined
            hide-details
            clearable
            :prepend-inner-icon="icons.mdiMagnify"
            style="max-width: 220px;"
            @change="onFilterChange"
            @keyup.enter="onFilterChange"
          ></v-text-field>

          <v-select
            v-model="selectedYear"
            :items="availableYears"
            label="Année"
            dense
            outlined
            hide-details
            clearable
            style="max-width: 120px;"
            @change="onFilterChange"
          ></v-select>

          <v-select
            v-model="selectedMonth"
            :items="months"
            item-text="text"
            item-value="value"
            label="Mois"
            dense
            outlined
            hide-details
            clearable
            style="max-width: 140px;"
            @change="onFilterChange"
          ></v-select>

          <v-btn icon color="primary" :loading="loading" @click="fetchData(false)">
            <v-icon>{{ icons.mdiRefresh }}</v-icon>
          </v-btn>

          <v-btn
            v-if="embedded"
            color="primary"
            class="rounded-pill font-weight-bold px-4"
            small
            depressed
            :loading="syncing"
            @click="openConfirmSyncModal"
          >
            <v-icon left small color="white">{{ icons.mdiRefresh }}</v-icon>
            Synchroniser les données
          </v-btn>
        </div>
      </div>

      <!-- Data Table -->
      <v-data-table
        :headers="computedHeaders"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="totalItems"
        class="custom-table mt-4"
        dense
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      >
        <template v-slot:item.site_name="{ item }">
          <span class="font-weight-bold primary--text text-uppercase">{{ item.site_name || '-' }}</span>
        </template>

        <template v-slot:item.month="{ item }">
          <span class="text-capitalize">{{ getMonthName(item.month) }}</span>
        </template>

        <!-- Dynamic Slots for Metrics (to show Overrides) -->
        <template v-for="header in metricHeaders" v-slot:[`item.${header.value}`]="{ item }">
          <div :key="header.value" class="py-1">
            <template v-if="hasOverride(item, header.value)">
              <div class="text-caption grey--text text-decoration-line-through mb-n1">{{ formatValue(item[header.value], header.value) }}</div>
              <div class="font-weight-bold primary--text d-flex align-center">
                {{ formatValue(getOverrideValue(item, header.value), header.value) }}
                <v-chip x-small color="warning" outlined class="ml-2 px-1" style="font-size: 0.6rem; height: 16px;">Manuelle</v-chip>
              </div>
            </template>
            <template v-else>
              <span class="font-weight-medium">{{ formatValue(item[header.value], header.value) }}</span>
            </template>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon small color="primary" class="edit-btn" @click="openOverrideModal(item)">
            <v-icon small color="primary">{{ icons.mdiPencil }}</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <gss-override-modal
      v-model="showModal"
      :item="selectedItem"
      :dataset-name="datasetName"
      @saved="onOverrideSaved"
      @error="onOverrideError"
    />

    <!-- Confirmation Dialog Synchronisation GSS -->
    <rmr-confirm-dialog
      v-model="confirmSyncModal"
      title="Confirmation de la synchronisation"
      confirm-label="Synchroniser"
      cancel-label="Annuler"
      @confirm="executeConfirmSync"
    >
      <template #message>
        Êtes-vous sûr de vouloir synchroniser les données de l'onglet <strong>{{ pageTitle }}</strong> depuis le fichier Google Spreadsheet ?
      </template>
    </rmr-confirm-dialog>

    <!-- Toast Notification -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top right class="font-weight-bold">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Fermer</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>

import autoEmailService from "@/services/autoEmailReport.service";
import RmrPageHero from "@/components/ui/layout/RmrPageHero.vue";
import RmrConfirmDialog from "@/components/ui/actions/RmrConfirmDialog.vue";
import GssOverrideModal from "./GssOverrideModal.vue";
import { useGssSync } from "./useGssSync";
import {
  mdiDatabaseSync,
  mdiRefresh,
  mdiPencil,
  mdiMagnify,
  mdiAccount,
  mdiAlertCircle,
  mdiChevronUp,
  mdiChevronDown,
  mdiRobot,
} from "@mdi/js";

export default {
  name: "GssDatasetView",
  components: {
    RmrPageHero,
    RmrConfirmDialog,
    GssOverrideModal,
  },
  props: {
    datasetName: { type: String, required: true },
    title: { type: String, default: "" },
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      isInitialized: false,
      gssSyncState: null,
      confirmSyncModal: false,
      icons: {
        mdiDatabaseSync,
        mdiRefresh,
        mdiPencil,
        mdiMagnify,
        mdiAccount,
        mdiAlertCircle,
        mdiChevronUp,
        mdiChevronDown,
        mdiRobot,
      },
      items: [],
      totalItems: 0,
      loading: false,
      saving: false,
      lastFetchRequestId: 0,
      lastHistoryRequestId: 0,
      searchSite: "",
      selectedYear: null,
      availableYears: [],
      selectedMonth: null,
      options: {
        page: 1,
        itemsPerPage: 25,
      },
      showModal: false,
      selectedItem: null,
      overrideForm: {},
      showGssSyncHistory: true,
      loadingGssSyncHistory: false,
      gssSyncHistoryList: [],
      gssSyncHistoryHeaders: [
        { text: "DATE / HEURE", value: "created_at", width: "170px" },
        { text: "UTILISATEUR", value: "triggered_by", width: "190px" },
        { text: "ONGLET", value: "target_tab", width: "200px" },
        { text: "STATUT", value: "status", width: "150px" },
      ],
      snackbar: {
        show: false,
        text: "",
        color: "info",
      },
      months: [
        { text: "Janvier", value: 1 },
        { text: "Février", value: 2 },
        { text: "Mars", value: 3 },
        { text: "Avril", value: 4 },
        { text: "Mai", value: 5 },
        { text: "Juin", value: 6 },
        { text: "Juillet", value: 7 },
        { text: "Août", value: 8 },
        { text: "Septembre", value: 9 },
        { text: "Octobre", value: 10 },
        { text: "Novembre", value: 11 },
        { text: "Décembre", value: 12 },
      ],
    };
  },
  computed: {
    pageTitle() {
      if (this.title) return this.title;
      const titles = {
        obj: "Objectifs",
        generale: "Général",
        generale_n1: "Général N-1",
        revenus_supp: "Revenus Supp.",
        acquisition: "Acquisition",
        finance: "Finance",
      };
      return titles[this.datasetName] || `Données ${this.datasetName}`;
    },
    computedHeaders() {
      if (!this.items.length) {
        return [
          { text: "SITE", value: "site_name" },
          { text: "PARTENAIRE", value: "partner_name" },
          { text: "ANNÉE", value: "year" },
          { text: "MOIS", value: "month" },
          { text: "DATE", value: "date_val" },
          { text: "ACTIONS", value: "actions", sortable: false, align: "right" },
        ];
      }
      const keysSet = new Set();
      this.items.forEach((item) => {
        Object.keys(item).forEach((k) => {
          if (!["id", "site_id", "raw_data", "manual_overrides", "created_at", "updated_at", "last_synced_at"].includes(k)) {
            keysSet.add(k);
          }
        });
      });
      const keys = Array.from(keysSet);

      const priorityOrder = ["site_name", "partner_name", "year", "month", "date_val", "date"];
      const orderedKeys = [];

      priorityOrder.forEach((pKey) => {
        if (keys.includes(pKey)) {
          orderedKeys.push(pKey);
        }
      });

      keys.forEach((k) => {
        if (!orderedKeys.includes(k)) {
          orderedKeys.push(k);
        }
      });

      const cols = orderedKeys.map((k) => ({
        text: this.getFieldLabel(k),
        value: k,
      }));
      cols.push({ text: "ACTIONS", value: "actions", sortable: false, align: "right", width: "80px" });
      return cols;
    },
    metricHeaders() {
      return this.computedHeaders.filter(h => !['site_name', 'partner_name', 'year', 'month', 'date_val', 'date', 'actions'].includes(h.value));
    },
    syncing() {
      return this.gssSyncState ? this.gssSyncState.value : false;
    }
  },
  watch: {
    options: {
      handler() {
        if (this.isInitialized) {
          this.fetchData();
        }
      },
      deep: true,
    },
    datasetName: {
      immediate: true,
      async handler(val) {
        this.searchSite = "";
        this.selectedMonth = null;
        if (val === "generale_n1") {
          this.selectedYear = new Date().getFullYear() - 1;
        } else {
          this.selectedYear = new Date().getFullYear();
        }
        await this.fetchYears();
        await this.fetchGssSyncHistory();
        if (this.options && this.options.page !== 1) {
          this.options.page = 1;
        } else {
          await this.fetchData();
        }
        this.isInitialized = true;
      },
    },
  },
  created() {
    const { syncingSpreadsheet, syncSpreadsheetData, checkSyncStatus, stopSyncPolling } = useGssSync(
      (text, color) => this.showToast(text, color),
      async () => {
        await this.fetchData(true);
        await this.fetchGssSyncHistory(true);
      },
      async () => {
        await this.fetchGssSyncHistory(true);
      },
      async () => {
        await this.fetchGssSyncHistory(true);
      },
      async () => {
        await this.fetchGssSyncHistory(true);
      }
    );
    this.gssSyncState = syncingSpreadsheet;
    this.executeSync = syncSpreadsheetData;
    this.stopSyncPolling = stopSyncPolling;
    checkSyncStatus();
  },
  beforeDestroy() {
    if (this.stopSyncPolling) this.stopSyncPolling();
  },
  methods: {
    isValidNumber(val) {
      return val !== undefined && val !== null && val !== "" && !Number.isNaN(val);
    },
    onFilterChange() {
      if (this.options.page !== 1) {
        this.options.page = 1;
      } else {
        this.fetchData();
      }
    },
    async fetchYears() {
      const currentDs = this.datasetName;
      try {
        const res = await autoEmailService.getGssDatasetYears(currentDs);
        if (this.datasetName !== currentDs) return;
        this.availableYears = res.data.years || [];
        if (this.availableYears.length > 0 && !this.availableYears.includes(this.selectedYear)) {
          this.selectedYear = this.availableYears.includes(new Date().getFullYear()) 
            ? new Date().getFullYear() 
            : this.availableYears[0];
        }
      } catch (err) {
        console.error("Erreur récupérations des années :", err);
      }
    },
    async fetchData(silent = false) {
      if (!silent) this.loading = true;
      const currentDs = this.datasetName;
      const reqId = ++this.lastFetchRequestId;
      try {
        const sortBy = (this.options && this.options.sortBy && this.options.sortBy.length > 0) ? this.options.sortBy[0] : undefined;
        const sortDesc = (this.options && this.options.sortDesc && this.options.sortDesc.length > 0) ? this.options.sortDesc[0] : undefined;

        const params = {
          dataset_name: this.datasetName,
          year: this.selectedYear || undefined,
          month: this.selectedMonth || undefined,
          site_name: this.searchSite || undefined,
          page: (this.options && this.options.page) || 1,
          page_size: (this.options && this.options.itemsPerPage) || 25,
          sort_by: sortBy,
          sort_desc: sortDesc,
        };
        const res = await autoEmailService.getGssDataset(params);
        if (this.datasetName !== currentDs || this.lastFetchRequestId !== reqId) return;
        this.items = res.data.items || [];
        this.totalItems = res.data.total || 0;
      } catch (err) {
        if (this.datasetName !== currentDs || this.lastFetchRequestId !== reqId) return;
        console.error("Erreur lors de la récupération :", err);
      } finally {
        if (this.datasetName === currentDs && this.lastFetchRequestId === reqId && !silent) {
          this.loading = false;
        }
      }
    },
    openConfirmSyncModal() {
      this.confirmSyncModal = true;
    },
    executeConfirmSync() {
      this.confirmSyncModal = false;
      this.syncData();
    },
    async syncData() {
      if (this.executeSync) {
        await this.executeSync(null, this.datasetName);
        await this.fetchGssSyncHistory(true);
      }
    },
    showToast(text, color = "info") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    getFieldLabel(key) {
      const frLabels = {
        "site_name": "SITE",
        "partner_name": "PARTENAIRE",
        "year": "ANNÉE",
        "month": "MOIS",
        "date_val": "DATE",
        "date": "DATE",
        "ca_reel": "CA RÉEL",
        "objectif_ca": "OBJECTIF CA",
        "objectif_rpm": "OBJECTIF RPM",
        "objectif_sessions": "OBJECTIF SESSIONS",
        "ca": "CA",
        "rpm": "RPM",
        "n_ca": "N/CA",
        "ca_n1": "CA N-1",
        "rpm_n1": "RPM N-1",
        "n_ca_n1": "N/CA N-1",
        "pvs_wys": "PVS WYS",
        "sessions_wys": "SESSIONS WYS",
        "pvs_wys_n1": "PVS WYS N-1",
        "sessions_wys_n1": "SESSIONS WYS N-1",
        "sessions": "SESSIONS",
        "montant": "MONTANT",
        "budget": "BUDGET"
      };
      if (frLabels[key]) return frLabels[key];
      return key.toUpperCase().replace(/_/g, " ");
    },
    getMonthName(monthNum) {
      const match = this.months.find(m => m.value === parseInt(monthNum));
      return match ? match.text : monthNum;
    },
    formatValue(val, key) {
      if (val === null || val === undefined || val === "") return "-";
      if (key === "date_val" || key === "date") {
        const isoStr = String(val).split("T")[0];
        const parts = isoStr.split("-");
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return val;
      }
      if (typeof val === "number") {
        const formatted = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(val);
        if (["ca", "ca_reel", "objectif_ca", "ca_n1", "montant", "budget"].includes(key)) {
          return `${formatted} €`;
        }
        return formatted;
      }
      return val;
    },
    hasOverride(item, field) {
      return item.manual_overrides && this.isValidNumber(item.manual_overrides[field]);
    },
    getOverrideValue(item, field) {
      return item.manual_overrides ? item.manual_overrides[field] : null;
    },
    openOverrideModal(item) {
      this.selectedItem = item;
      this.showModal = true;
    },
    onOverrideSaved() {
      this.showToast("Surcharges enregistrées avec succès", "success");
      this.fetchData();
    },
    onOverrideError(msg) {
      this.showToast(msg || "Erreur lors de la sauvegarde", "error");
    },
    async fetchGssSyncHistory(silent = false) {
      if (!silent) this.loadingGssSyncHistory = true;
      const currentDs = this.datasetName;
      const reqId = ++this.lastHistoryRequestId;
      try {
        const res = await autoEmailService.getGssSyncHistory({ target_tab: this.datasetName, limit: 50 });
        if (this.datasetName !== currentDs || this.lastHistoryRequestId !== reqId) return;
        this.gssSyncHistoryList = res.data || [];
      } catch (err) {
        if (this.datasetName !== currentDs || this.lastHistoryRequestId !== reqId) return;
        console.error("Erreur lors de la récupération de l'historique de synchro GSS :", err);
      } finally {
        if (this.datasetName === currentDs && this.lastHistoryRequestId === reqId && !silent) {
          this.loadingGssSyncHistory = false;
        }
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "-";
      try {
        const d = new Date(dateStr);
        return d.toLocaleString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (e) {
        return dateStr;
      }
    },
    formatTabName(val) {
      if (!val) return "Tous les onglets (Globale)";
      const raw = val.trim().toLowerCase();
      const map = {
        "generale_n1": "Général N-1",
        "generale-n1": "Général N-1",
        "général n-1": "Général N-1",
        "general n-1": "Général N-1",
        "generale": "Général",
        "general": "Général",
        "général": "Général",
        "revenus_supp": "Revenus Supp.",
        "revenus-supp": "Revenus Supp.",
        "revenus supp": "Revenus Supp.",
        "objectifs": "Objectifs",
        "obj": "Objectifs",
        "acquisition": "Acquisition",
        "finance": "Finance",
      };
      return map[raw] || val;
    },
    isSystemTriggered(item) {
      if (!item) return false;
      const trig = (item.triggered_by || item.user_name || "").toLowerCase();
      return item.action_type === "scheduled" || trig.includes("système") || trig.includes("systeme");
    },
  },
};
</script>

<style scoped>
.shadow-btn {
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3) !important;
}

.edit-btn {
  background-color: #f3f4f6;
  border-radius: 8px;
}

.embedded-card-border {
  border: 1px solid #e2e8f0 !important;
}
</style>
